import * as React from "react";
import { observer } from "mobx-react-lite";
import { classNames } from "src/common/classNames";
import styles from "./Pl2GanttView.module.less";
import { Planning2GanttStore } from "../Planning2GanttStore";
import { Bar } from "./GanttViewTypes";

type PropsPl2GanttViewHeader = {
  gantt: Planning2GanttStore;
};

type Text = {
  key: string;
  text: string;
  cx: number;
  cy: number;
  cls?: string;
};

type Ctx = {
  readonly height: number;
  readonly middle: number;
  bars: Bar[];
  labels: Text[];
  grid: [number, number, number, number][];
};

const drawDays = (gantt: Planning2GanttStore, ctx: Ctx) => {
  const { days, dayWidth } = gantt;
  const { middle, height, bars, labels, grid } = ctx;
  days.forEach((d, i) => {
    const x = dayWidth * i;

    // левая стенка ячейки
    grid.push([x, middle, x, height]);

    // фоновая заливка для ячейки
    if (d.bk) {
      bars.push({
        x,
        y: middle,
        width: dayWidth,
        height: height - middle,
        key: `bar-${d.key}`,
        cls: d.bk,
      });
    }
    // число
    if (dayWidth >= 14) {
      // здесь пока условие показа дат вычислено опытным путём
      labels.push({
        key: `day-${d.key}`,
        cls: d.bk === "today" ? "headTodayNumber" : undefined,
        text: String(d.date.date()),
        cx: x + dayWidth / 2,
        cy: (height * 3) / 4,
      });
    }
  });
};

const drawMonths = (
  gantt: Planning2GanttStore,
  ctx: Ctx,
  y1: number,
  y2: number,
  useYear: boolean,
) => {
  const { months } = gantt;
  const { grid, labels } = ctx;
  const cy = (y1 + y2) / 2;
  months.forEach((m, i) => {
    if (i > 0) {
      grid.push([m.x, y1, m.x, y2]);
    }
    const cx = m.x + m.width / 2;
    let text = m.name;
    if (useYear) {
      text += `, ${m.year}`;
    }
    labels.push({
      cx,
      cy,
      text,
      key: m.key,
    });
  });
};

const drawLongWeeks = (gantt: Planning2GanttStore, ctx: Ctx) => {
  const { middle, labels, grid } = ctx;
  const cy = middle / 2;
  gantt.weeks.forEach(({ key, x, width, begin, end }, i) => {
    if (i > 0) grid.push([x, 0, x, middle]);
    if (width > 90) {
      const cx = x + width / 2;
      const d1 = begin.date();
      const m1 = Planning2GanttStore.monthName(begin);
      const y1 = begin.year() % 100;
      const d2 = end.date();
      const m2 = Planning2GanttStore.monthName(end);
      const y2 = end.year() % 100;
      // todo: это неудачное решение. потому что это формат только для России. ну и размер
      let text = `${d1}`;
      if (m1 !== m2 || y1 !== y2) text += ` ${m1}`;
      if (y1 !== y2) text += ` ${y1}`;
      text += ` - ${d2} ${m2} ${y2}`;
      labels.push({ key, text, cy, cx });
    }
  });
};

const drawShortWeeks = (gantt: Planning2GanttStore, ctx: Ctx) => {
  const { middle, height, labels, grid } = ctx;
  const cy = middle * 1.5;
  gantt.weeks.forEach(({ key, x, width, begin }, i) => {
    if (i > 0) grid.push([x, middle, x, height]);
    const text = String(begin.week());
    const cx = x + width / 2;
    labels.push({ key, text, cx, cy });
  });
};

const drawYears = (gantt: Planning2GanttStore, ctx: Ctx) => {
  const { middle, labels, grid } = ctx;
  const cy = middle / 2;
  gantt.years.forEach(({ key, year, x, width }, i) => {
    labels.push({
      key,
      text: String(year),
      cy,
      cx: x + width / 2,
    });
    if (i > 0) {
      grid.push([x, 0, x, middle]);
    }
  });
};

const renderDaysMode = (gantt: Planning2GanttStore, ctx: Ctx) => {
  const { middle } = ctx;
  drawMonths(gantt, ctx, 0, middle, true);
  drawDays(gantt, ctx);
};

const renderWeekMode = (gantt: Planning2GanttStore, ctx: Ctx) => {
  drawLongWeeks(gantt, ctx);
  drawDays(gantt, ctx);
};

const renderRealWeekMode = (gantt: Planning2GanttStore, ctx: Ctx) => {
  const { middle } = ctx;
  drawMonths(gantt, ctx, 0, middle, true);
  drawShortWeeks(gantt, ctx);
};

const renderMonthsMode = (gantt: Planning2GanttStore, ctx: Ctx) => {
  const { middle, height } = ctx;
  drawYears(gantt, ctx);
  drawMonths(gantt, ctx, middle, height, false);
};

export const Pl2GanttViewHeader: React.FC<PropsPl2GanttViewHeader> = observer(
  ({ gantt }) => {
    const { totalWidth, scaleMode } = gantt;
    const [shortWeeks, setShortWeeks] = React.useState(true);
    React.useEffect(() => {
      const onKey = (ev: KeyboardEvent) => {
        if (ev.code === "F8") {
          setShortWeeks((prev) => !prev);
        }
      };
      window.addEventListener("keyup", onKey);
      return () => {
        window.removeEventListener("keyup", onKey);
      };
    }, []);
    const height = Planning2GanttStore.headHeight;
    const middle = Math.round(height / 2);
    const ctx: Ctx = {
      middle,
      height,
      bars: [],
      labels: [],
      grid: [[0, middle, totalWidth + 1, middle]],
    };
    switch (scaleMode) {
      case "day":
        renderDaysMode(gantt, ctx);
        break;
      case "week":
        if (shortWeeks) {
          renderRealWeekMode(gantt, ctx);
        } else {
          renderWeekMode(gantt, ctx);
        }
        break;
      case "month":
        renderMonthsMode(gantt, ctx);
        break;
      default:
        break;
    }

    const { grid, bars, labels } = ctx;

    const gridPath = grid
      .map(([x1, y1, x2, y2]) => `M${x1} ${y1} L${x2} ${y2}`)
      .join(" ");
    return (
      <svg
        width={`${totalWidth}px`}
        height={`${height}px`}
        viewBox={`0 0 ${totalWidth} ${height}`}
      >
        {bars.map(({ x, y, width, height: h, key, cls }) => (
          <rect
            key={key}
            x={x}
            y={y}
            width={width}
            height={h}
            className={styles[cls]}
          />
        ))}
        {labels.map(({ cx, cy, text, cls, key }) => (
          <text
            key={key}
            x={cx}
            y={cy}
            className={classNames([styles.headDayNumber, cls && styles[cls]])}
          >
            {text}
          </text>
        ))}
        <path d={gridPath} className={styles.grid} />
      </svg>
    );
  },
);
